import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Progress } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

const STAGES = [
  { label: 'Preparing your request…', target: 18 },
  { label: 'Sending email content to AI…', target: 38 },
  { label: 'Running selected prompt…', target: 62 },
  { label: 'Generating response…', target: 84 },
  { label: 'Finalizing output…', target: 92 },
] as const;

export interface AiAssistantProgressPanelProps {
  promptName?: string;
  compact?: boolean;
  className?: string;
}

const AiAssistantProgressPanel: React.FC<AiAssistantProgressPanelProps> = ({
  promptName,
  compact = false,
  className,
}) => {
  const { colors } = useTheme();
  const [stageIndex, setStageIndex] = useState(0);
  const [percent, setPercent] = useState(4);

  const stage = STAGES[Math.min(stageIndex, STAGES.length - 1)];

  useEffect(() => {
    setStageIndex(0);
    setPercent(4);
  }, [promptName]);

  useEffect(() => {
    const tick = window.setInterval(() => {
      setPercent((prev) => {
        const target = STAGES[Math.min(stageIndex, STAGES.length - 1)].target;
        if (prev >= target) return prev;
        const step = prev < 40 ? 1.6 : prev < 75 ? 0.85 : 0.3;
        return Math.min(target, prev + step);
      });
    }, 160);
    return () => window.clearInterval(tick);
  }, [stageIndex]);

  useEffect(() => {
    if (stageIndex >= STAGES.length - 1) return;
    const advance = window.setTimeout(() => {
      setStageIndex((i) => Math.min(i + 1, STAGES.length - 1));
    }, 4000);
    return () => window.clearTimeout(advance);
  }, [stageIndex]);

  return (
    <div
      className={cn(
        'rounded-xl border border-indigo-500/35 bg-gradient-to-br from-indigo-500/10 via-violet-500/5 to-transparent',
        compact ? 'px-3 py-2.5' : 'px-3 py-3 sm:px-4',
        colors.border,
        className,
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
          <Sparkles className="h-4 w-4 animate-pulse" aria-hidden />
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className={cn('text-sm font-semibold', colors.text)}>AI assistant working</p>
            <span className={cn('text-xs tabular-nums', colors.textMuted)}>{Math.round(percent)}%</span>
          </div>

          {promptName ? (
            <p className={cn('text-xs font-medium truncate', colors.textSecondary)}>{promptName}</p>
          ) : null}

          <p className={cn(compact ? 'text-xs' : 'text-xs sm:text-sm', colors.textSecondary)}>{stage.label}</p>

          <Progress value={percent} size="sm" className="w-full" />

          {!compact ? (
            <p className={cn('text-[11px]', colors.textMuted)}>
              Longer emails or complex prompts may take up to a minute.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AiAssistantProgressPanel;
